import React, {Component, ReactNode} from 'react';
import ComponentName from '../constant/ComponentName';
import {dispatch, RootState} from '../redux/store';
import {ComponentState, setSelectedItems} from '../redux/actions/ComponentActions';
import {changePageFilter, clearPage} from '../redux/actions/PageActions';
import {connect, ConnectedProps} from 'react-redux';
import Modal, {enrichModel} from '../components/folder/view/Modal';
import StandardItemBuilderView from '../components/folder/view/item/StandardItemBuilderView';
import ItemBuilderView from '../components/folder/view/item/ItemBuilderView';
import _ from 'lodash';
import DraggableItemService from '../components/folder/DraggableItemService';
import ServiceContainer from '../config/ServiceContainer';
import {ServiceNames} from '../constant/ServiceNames';
import ValueFactory from '../util/ValueFactory';
import {PagePath} from '../redux/reducers/PageReducer';
import {query, repository, subscribe} from '@trlogic/client-js';
import Breadcrumb from '../components/Breadcrumb';
import FolderView from '../components/folder/view/FolderView';
import FolderContainer from '../pages/FolderContainer';
import ItemToolsMenu from '../components/ItemToolsMenu';
import CustomPaginator, {CustomPaginatorProps} from '../components/custom/CustomPaginator';
import CustomLoading from '../components/custom/CustomLoading';
import {FormicaProps} from './withFormica';
import CollectionNames from '../constant/CollectionNames';
import {InstanceContainer} from '../config/binding/InstanceBinding';
import Injectables from '../constant/Injectables';
import {generateUUIDV4} from 'util/Generator';
import {CustomFilterFields} from '../domain/ui/FilterDomains';
import FilterUtil from '../util/FilterUtil';
import DefaultFilterUtil from '../util/DefaultFilterUtil';
import {DefaultFilter, PermissionType, UserSystemAttributes} from '../domain/rbac/RbacDomains';
import RoleHelper from '../RoleHelper';
import {AccessBase, DeployableAccessBase} from '../domain/access/BaseAccess';
import BulkActionUtil from '../util/BulkActionUtil';
import DeployErrorUtil from '../util/DeployErrorUtil';
import LanguageUtil from '../util/LanguageUtil';
import {openBulkActionDialog} from '../redux/actions/BulkActionActions';
import {confirmUndeployWithImpact} from '../components/deploy/UndeployImpactDialog';
import {BulkActionDependency, BulkActionDependencyError, BulkActionType} from '../redux/reducers/BulkActionReducer';
import {EnrichedComponentCategory} from '../domain/category/CategoryOperationDomains';
import {ItemType} from '../components/folder/view/Item';
import Filter = query.Filter;
import empty = query.empty;
import Order = query.Order;
import inside = query.inside;
import and = query.and;
import Subscription = subscribe.Subscription;
import Computation = subscribe.Computation;
import QueryBuilder = query.QueryBuilder;

abstract class StandardContainerComponent<TProps, TState, TItem extends AccessBase & { name: string }, TItemSchema> extends Component<TProps & DefaultProps, DefaultState<TItem, TItemSchema>> {
  public state: DefaultState<TItem, TItemSchema> = new DefaultState<TItem, TItemSchema>() as DefaultState<TItem, TItemSchema>;

  protected readonly args: StandardSubscriptionComponentArgs;

  private readonly draggableItemService: DraggableItemService = ServiceContainer.getInstance().get(ServiceNames.DRAGGABLE_ITEM);
  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  protected itemSubscription: Subscription | undefined;
  protected computation: Computation | undefined;

  private stoppingStarted = false;

  protected readonly id: string = generateUUIDV4();

  protected constructor(props: Readonly<TProps & DefaultProps>, args: StandardSubscriptionComponentArgs) {
    super(props);
    this.args = args;
    this.prepareFilterFields();
  }

  public async componentWillUnmount(): Promise<void> {
    dispatch(clearPage());
    this.state.itemBuilderView?.terminate();
    await this.stopTracker();
  }

  public componentDidUpdate(prevProps: Readonly<TProps & DefaultProps>, prevState: typeof this.state) {
    const {
      currentCategory,
      component: {selectedComponentIds},
    } = this.props;
    const {items, itemBuilderView, selectedFolders} = this.state;
    if (this.isQueryChanged(prevProps) || this.isCategoryChanged(prevProps)) {
      this.resubscribeTracker();
    }
    if (items != undefined && items.length > 0 && prevState.items != items && currentCategory != undefined) {
      this.initializeItemBuilderView();
    }
    if (itemBuilderView != undefined && itemBuilderView?.getLastTarget() != undefined && itemBuilderView?.getLastCurrent() != undefined && currentCategory) {
      this.draggableItemService.viewsDidUpdate(
        itemBuilderView,
        itemBuilderView.getLastCurrent()!,
        itemBuilderView.getLastTarget()!,
        this.args.component,
        currentCategory,
        selectedFolders,
        selectedComponentIds,
        this.setSelectedItems,
        this.setSelectedFolders,
      );
      itemBuilderView.resetLastUpdatedView();
    }
  }

  protected initializeItemBuilderView = () => {
    const {item} = this.state;
    item.children = [];
    this.prepareListView();
    this.prepareFolderView();
    this.prepareBreadcrumbView();
    const modal: Modal = enrichModel(item, undefined);
    this.state.itemBuilderView?.terminate();
    const itemBuilderView = new StandardItemBuilderView({
      el: 'outlet',
      model: modal,
    });
    itemBuilderView.initialize();
    itemBuilderView.onChange((modal: Modal) => {
      this.setItem(modal);
    });
    this.setState({itemBuilderView});
  };

  public setItem = (modal: Modal) => {
    this.setState({item: modal});
  };

  private isCategoryChanged = (prevProps: Readonly<TProps & DefaultProps>) => {
    const {currentCategory} = this.props;
    if (prevProps.currentCategory != undefined && currentCategory != undefined) {
      return !_.isEqual(prevProps.currentCategory, currentCategory);
    }
    return prevProps.currentCategory != undefined || currentCategory != undefined;
  };

  private isQueryChanged = (prevProps: Readonly<TProps & DefaultProps>) => {
    return prevProps.router.location.search != this.props.router.location.search;
  };

  protected setSelectedFolders = (folders: Modal[]) => {
    this.setState({selectedFolders: folders});
  };

  private setSelectedItems = (items: Modal[]) => {
    const {setSelectedItems} = this.props;
    setSelectedItems(items);
  };

  private prepareListView = () => {
    const {items, item} = this.state;
    items.map((_item: TItem) => {
      const itemModal = {
        id: ValueFactory.encodeBase64(_item.name),
        children: undefined,
        value: {
          type: ItemType.LIST,
          payload: _item,
        },
      };
      item.children?.push(itemModal);
    });
  };

  private prepareFolderView = () => {
    const {currentCategory} = this.props;
    const {item} = this.state;
    if (currentCategory != undefined && currentCategory.categories != undefined && currentCategory.categories.length > 0) {
      currentCategory.categories.map((category: EnrichedComponentCategory) => {
        const folderModel = {
          id: category.id,
          value: {
            type: ItemType.FOLDER,
            payload: category,
          },
        };
        item.children?.push(folderModel);
      });
    }
  };

  private prepareBreadcrumbView = () => {
    const {pagePath} = this.props;
    const {item} = this.state;
    pagePath.map((path: PagePath) => {
      if (path.payload) {
        const breadcrumbModel = {
          id: path.payload.id,
          children: undefined,
          value: {
            type: ItemType.BREADCRUMB,
            payload: path.payload,
            title: path.title,
          },
        };
        item.children?.push(breadcrumbModel);
      }
    });
  };

  private getSorts = (sorts: Order[]): Order[] => {
    if (sorts.length == 0) {
      return [
        {
          field: 'updatedAt',
          operator: 'DESC',
        },
      ];
    }
    return sorts;
  };

  protected onPageChange = (event: CustomPaginatorProps) => {
    this.setState({rows: event.rows ?? this.state.rows, currentPage: event.page ?? this.state.currentPage}, () => {
      this.exportQueryParam();
      setTimeout(() => {
        this.resubscribeTracker();
      }, 0);
    });
  };

  protected loadQueryParam = async () => {
    return new Promise<void>((resolve) => {
      const {
        router: {location},
      } = this.props;
      const searchParams = new URLSearchParams(location.search);
      const page = searchParams.get('page');
      const row = searchParams.get('row');
      let parsedPage = page ? parseInt(page) : this.state.currentPage;
      let parsedRow = row ? parseInt(row) : this.state.rows;
      if (isNaN(parsedPage) || parsedPage < 1) parsedPage = this.state.currentPage;
      if (isNaN(parsedRow) || parsedRow < 1) parsedRow = this.state.rows;
      this.setState(
        {
          currentPage: parsedPage,
          rows: parsedRow,
        },
        resolve,
      );
    });
  };

  protected exportQueryParam = () => {
    const {
      router: {location, navigate},
    } = this.props;
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', this.state.currentPage.toString());
    searchParams.set('row', this.state.rows.toString());
    navigate(location.pathname + '?' + searchParams.toString());
  };

  protected onSortChange = (sorts: Order[]) => {
    this.setState({sorts}, this.resubscribeTracker);
  };

  protected openCreatePage = (e?: any) => {
    const {
      router: {navigate},
    } = this.props;
    navigate('create');
  };

  protected openComponent = (id: string) => {
    const {
      router: {navigate},
    } = this.props;
    navigate(`edit/${id}`);
  };

  protected onChangeFilter = (filter: Filter) => {
    const isSame = _.isEqual(filter, this.state.filter);
    if (isSame) return;
    this.setState({filter}, this.resubscribeTracker);
  };

  protected startTracker = async () => {
    const {currentCategory} = this.props;
    const {rows, currentPage, filter, sorts} = this.state;
    if (currentCategory == undefined) {
      return;
    }
    const categoryFilter = inside('id', currentCategory.components);
    const itemQuery: query.SelectQueryBuilder = query.QueryBuilder.select()
      .limit(rows)
      .skip((currentPage - 1) * rows)
      .where(filter)
      .orderBy(...this.getSorts(sorts))
      .from(this.args.itemCollection);
    const itemFilter = and(categoryFilter, itemQuery.build().filter || empty());
    itemQuery.where(itemFilter);
    this.itemSubscription = subscribe.Formica.subscribe(this.args.itemCollection, itemQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.itemSubscription]);
  };

  protected tracker = async () => {
    const {currentCategory} = this.props;
    const {sorts, filter, rows} = this.state;
    try {
      if (this.itemSubscription?.ready()) {
        const itemQuery: query.Query = query.QueryBuilder.select()
          .from(this.args.itemCollection)
          .orderBy(...this.getSorts(sorts))
          .where(filter)
          .limit(rows)
          .build();
        const items: TItem[] = await this.repository.findMany(itemQuery);
        await this.prepareCount();
        this.setState({items: items, loading: false}, () => {
          this.calculatePermissions();
          if (items.length <= 0 && currentCategory != undefined) {
            this.initializeItemBuilderView();
            this.setState({totalRecord: 0});
          }
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  protected stopTracker = async () => {
    if (this.stoppingStarted) {
      return;
    }
    this.stoppingStarted = true;
    await this.itemSubscription?.stop();
    this.computation?.stop();
  };

  protected resubscribeTracker = async () => {
    await this.loadQueryParam();
    if (this.computation == undefined) {
      await this.startTracker();
    } else {
      this.computation?.onStop(() => {
        this.stoppingStarted = false;
        this.startTracker();
      });
      await this.stopTracker();
    }
  };

  protected getCountFilter = () => {
    const {currentCategory} = this.props;
    const {filter} = this.state;
    const itemQuery = QueryBuilder.select().from(this.args.itemCollection).where(filter);
    const categoryFilter = inside('id', currentCategory.components);
    const itemFilter = and(categoryFilter, itemQuery.build().filter || empty());
    itemQuery.where(itemFilter);
    return itemQuery;
  };

  protected prepareFilterFields = async () => {
    const {attributes, component, user} = this.props;
    const fields: CustomFilterFields[] = await FilterUtil.getFields(this.args.component);
    const filter: DefaultFilter | undefined = component
      ? _.cloneDeep(
        DefaultFilterUtil.find(attributes?.filters, {
          component: this.args.component,
          riskType: user!.activeRiskType,
        }),
      )
      : undefined;
    dispatch(
      changePageFilter({
        filter: filter?.filter.filter || ValueFactory.initializeFilter(),
        items: fields,
      }),
    );
  };

  private calculatePermissions = () => {
    const {items, permissions} = this.state;
    for (const item of items) {
      RoleHelper.setCurrentAccess(item);
      permissions[item.name] = RoleHelper.getPermission(item);
      RoleHelper.clearCurrentAccess();
    }
    this.setState({permissions});
  };

  public render() {
    const {
      component: {selectedComponentIds},
    } = this.props;
    const {items, loading, itemBuilderView, selectedFolders, rows, totalRecord, currentPage, filter} = this.state;

    return (
      <div className={'w-full h-full flex flex-col gap-y-3 overflow-y-hidden '}>
        <Breadcrumb views={itemBuilderView?.getBreadCrumbViews() as FolderView[] | undefined}/>
        <div className={'border-b overflow-x-scroll overflow-y-hidden flex w-full shrink-0 mb-1.5'}>
          <FolderContainer
            key={this.args.component}
            path={''}
            views={itemBuilderView?.getFolderViews() as FolderView[] | []}
            setSelectedFolders={this.setSelectedFolders}
            selectedFolders={selectedFolders}
            component={this.args.component}
          />
        </div>
        <div className={'flex min-h-[24px] px-5 justify-start bg-white items-center w-full'}>
          <ItemToolsMenu
            views={true}
            id={`item-tools-${this.id}`}
            createFunc={this.openCreatePage}
            itemsIsSelected={selectedComponentIds.length > 0}
            deployAllFunc={this.args.withSchema ? () => this.handleDeployAll() : undefined}
            undeployAllFunc={this.args.withSchema ? () => this.handleUndeployAll() : undefined}
            removeAllFunc={() => this.handleRemoveAll()}
            component={this.args.component}
            onChangeFilter={this.onChangeFilter}
            componentLabel={this.args.label}
            items={items}
            filter={filter}
            exportVisibleAlways
            sessionCheck
          />
        </div>
        <CustomPaginator
          currentPage={currentPage}
          currentRecords={items.length}
          rows={rows}
          totalRecords={totalRecord}
          rowsPerPageOptions={[5, 10, 20, 50, 100, 200]}
          onPageChange={this.onPageChange}
        />
        <div className="flex h-full w-full px-5">{loading ? <CustomLoading/> : this.getListView()}</div>
      </div>
    );
  }

  protected handleDeployAll = async () => {
    const {
      component: {selectedComponentIds},
    } = this.props;
    const status = BulkActionUtil.analyzeSelectedItems(selectedComponentIds);
    const deployableItems = BulkActionUtil.getDeployableItems(selectedComponentIds);

    if (status.hasMixed && deployableItems.length > 0) {
      const selectedStatuses = this.getSelectedStatuses(selectedComponentIds);
      dispatch(openBulkActionDialog({
        actionType: 'DEPLOY' as BulkActionType,
        componentName: this.args.component,
        selectedStatuses,
        hasMixedStatuses: true,
        onConfirm: async () => {
          await this.executeBulkAction(() => this.deployAll(), 'DEPLOY');
        },
      }));
    } else if (deployableItems.length > 0) {
      await this.executeBulkAction(() => this.deployAll(), 'DEPLOY');
    }
  };

  protected handleUndeployAll = async () => {
    const {
      component: {selectedComponentIds},
    } = this.props;
    const status = BulkActionUtil.analyzeSelectedItems(selectedComponentIds);
    const undeployableItems = BulkActionUtil.getUndeployableItems(selectedComponentIds);

    if (status.hasMixed && undeployableItems.length > 0) {
      const selectedStatuses = this.getSelectedStatuses(selectedComponentIds);
      dispatch(openBulkActionDialog({
        actionType: 'UNDEPLOY' as BulkActionType,
        componentName: this.args.component,
        selectedStatuses,
        hasMixedStatuses: true,
        onConfirm: async () => {
          await this.confirmBulkUndeploy(undeployableItems);
        },
      }));
    } else if (undeployableItems.length > 0) {
      await this.confirmBulkUndeploy(undeployableItems);
    }
  };

  private confirmBulkUndeploy = async (items: DeployableAccessBase[]) => {
    const ids = this.readUndeployIds(items);
    await confirmUndeployWithImpact(this.args.component, ids, () => this.executeBulkAction(() => this.undeployAll(), 'UNDEPLOY'));
  };

  private readUndeployIds(items: DeployableAccessBase[]): string[] {
    const ids: string[] = [];
    for (const item of items) {
      const named = item as DeployableAccessBase & { id?: string; name?: string };
      if (named.name != undefined && named.name.trim().length > 0) {
        ids.push(named.name);
      }
      if (named.id != undefined && named.id.trim().length > 0 && named.id !== named.name) {
        ids.push(named.id);
      }
    }
    return ids;
  };

  protected handleRemoveAll = async () => {
    const {
      component: {selectedComponentIds},
    } = this.props;

    if (!this.args.withSchema) {
      if (selectedComponentIds.length > 0) {
        await this.executeBulkAction(() => this.removeAll(), 'REMOVE');
      }
      return;
    }

    const status = BulkActionUtil.analyzeSelectedItems(selectedComponentIds);
    const removableItems = BulkActionUtil.getRemovableItems(selectedComponentIds);

    if (status.hasMixed && removableItems.length > 0) {
      const selectedStatuses = this.getSelectedStatuses(selectedComponentIds);
      dispatch(openBulkActionDialog({
        actionType: 'REMOVE' as BulkActionType,
        componentName: this.args.component,
        selectedStatuses,
        hasMixedStatuses: true,
        onConfirm: async () => {
          await this.executeBulkAction(() => this.removeAll(), 'REMOVE');
        },
      }));
    } else if (removableItems.length > 0) {
      await this.executeBulkAction(() => this.removeAll(), 'REMOVE');
    }
  };

  protected async executeBulkAction(action: () => Promise<BulkActionDependencyError[]>, actionType: BulkActionType): Promise<void> {
    try {
      const errors = await action();
      this.handleBulkActionErrors(errors, actionType);
      dispatch(setSelectedItems([]));
    } catch (error: unknown) {
      if (DeployErrorUtil.show(error, this.resolveBulkActionSummary(actionType))) {
        return;
      }
      throw error;
    }
  }

  private resolveBulkActionSummary(actionType: BulkActionType): string {
    switch (actionType) {
      case 'DEPLOY':
        return LanguageUtil.get({id: 'common.label.deploy', defaultMessage: 'Deploy'});
      case 'UNDEPLOY':
        return LanguageUtil.get({id: 'common.label.undeploy', defaultMessage: 'Undeploy'});
      case 'REMOVE':
        return LanguageUtil.get({id: 'common.label.remove', defaultMessage: 'Remove'});
      default:
        return LanguageUtil.get({id: 'common.label.deploy', defaultMessage: 'Deploy'});
    }
  }

  protected handleBulkActionErrors = (errors: BulkActionErrorItem[], actionType: BulkActionType) => {
    if (errors == undefined || errors.length === 0) {
      return;
    }

    const notDeployed = errors.flatMap((item) => DeployErrorUtil.readDependencies(item.error) ?? []);
    if (notDeployed.length > 0) {
      DeployErrorUtil.showDependencies(notDeployed, LanguageUtil.get({id: 'common.label.deploy', defaultMessage: 'Deploy'}));
    }

    const itemsWithDependencies = errors.filter((item) => {
      if (DeployErrorUtil.readDependencies(item.error) != undefined) {
        return false;
      }
      const deps = item.error?.dependencies || item.dependencies;
      return deps != undefined && deps.length > 0;
    });

    if (itemsWithDependencies.length === 0) {
      return;
    }

    const dependencyErrors: BulkActionDependencyError[] = itemsWithDependencies.map((item) => {
      const deps = item.error?.dependencies || item.dependencies || [];
      return {
        id: item.id ?? '',
        dependencies: deps.map((dep) => ({
          componentType: dep.componentType,
          componentId: dep.componentId,
        })),
      };
    });

    dispatch(openBulkActionDialog({
      actionType,
      componentName: this.args.component,
      selectedStatuses: [],
      hasMixedStatuses: false,
      dependencyErrors,
      onConfirm: () => {
      },
    }));
  };

  protected getSelectedStatuses = (items: DeployableAccessBase[]): string[] => {
    const statuses = new Set<string>();
    for (const item of items) {
      if (item.stage) {
        statuses.add(item.stage);
      }
    }
    return Array.from(statuses);
  };

  protected abstract prepareCount(): Promise<void>;

  protected abstract getListView(): ReactNode;

  protected abstract deployAll(): Promise<BulkActionDependencyError[]>;

  protected abstract undeployAll(): Promise<BulkActionDependencyError[]>;

  protected abstract removeAll(): Promise<BulkActionDependencyError[]>;
}

export interface DefaultProps extends ReduxProps, FormicaProps {
}

interface BulkActionErrorItem {
  id?: string;
  error?: {
    reason?: string;
    dependencies?: BulkActionDependency[];
  };
  dependencies?: BulkActionDependency[];
}

export class DefaultState<TItem, TItemSchema> {
  items: TItem[] = [];
  item: Modal = {
    id: 'Main',
    children: [],
  };
  itemBuilderView: ItemBuilderView | undefined;
  selectedFolders: Modal[] = [];
  filter: Filter = empty();
  loading: boolean = true;
  rows: number = 10;
  totalRecord: number = 0;
  currentPage: number = 1;
  sorts: Order[] = [];
  permissions: Record<string, PermissionType> = {};
}

const mapStateToProps = (state: RootState) => ({
  itemView: state.page.itemView,
  currentCategory: state.page.currentCategory,
  pagePath: state.page.pagePath,
  component: state.component as ComponentState,
  user: state.auth.user,
  attributes: state.auth.attributes as UserSystemAttributes,
});

const mapDispatchToProps = {setSelectedItems, changePageFilter};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export type StandardSubscriptionComponentArgs = {
  component: ComponentName;
  itemCollection: CollectionNames;
  label: string;
  withSchema?: boolean;
};

export default StandardContainerComponent;
